
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Card from '../../components/base/Card';

export default function CertificateLookup() {
  const [lookupMethod, setLookupMethod] = useState<'email' | 'code'>('email');
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    name: '',
    birthDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [certificate, setCertificate] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // 증명서 조회 로직
    setTimeout(() => {
      setLoading(false);
      // 예시 증명서 데이터
      setCertificate({
        id: 'CERT-2024-001',
        studentName: '김학습',
        courseName: '디지털 마케팅 전문가 과정',
        institution: '한국디지털교육원',
        completionDate: '2024-01-15',
        issueDate: '2024-01-20',
        validUntil: '2026-01-20',
        grade: 'A+',
        isPaid: true,
        downloadCount: 0,
        maxDownloads: 3
      });
    }, 2000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDownload = () => {
    if (certificate.isPaid && certificate.downloadCount >= certificate.maxDownloads) {
      alert('다운로드 횟수를 초과했습니다. 추가 다운로드를 원하시면 결제해주세요.');
      return;
    }
    
    // PDF 다운로드 로직
    alert('증명서 PDF가 다운로드됩니다.');
    setCertificate({
      ...certificate,
      downloadCount: certificate.downloadCount + 1
    });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">증명서 조회</h1>
            <p className="text-lg text-gray-600">
              이메일 링크나 인증코드로 증명서를 조회하고 출력하세요.
            </p>
          </div>

          {!certificate ? (
            <div className="max-w-2xl mx-auto">
              <Card>
                {/* 조회 방법 선택 */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">조회 방법 선택</h3>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setLookupMethod('email')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                        lookupMethod === 'email' 
                          ? 'border-blue-600 bg-blue-50 text-blue-600' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <i className="ri-mail-line text-2xl mb-2"></i>
                      <div className="font-medium">이메일 링크</div>
                      <div className="text-sm text-gray-600">이메일로 받은 링크 사용</div>
                    </button>
                    <button
                      onClick={() => setLookupMethod('code')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                        lookupMethod === 'code' 
                          ? 'border-blue-600 bg-blue-50 text-blue-600' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <i className="ri-key-line text-2xl mb-2"></i>
                      <div className="font-medium">인증코드</div>
                      <div className="text-sm text-gray-600">인증코드 직접 입력</div>
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {lookupMethod === 'email' ? (
                    <Input
                      label="이메일 주소"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="증명서를 받은 이메일 주소를 입력하세요"
                      required
                    />
                  ) : (
                    <>
                      <Input
                        label="인증코드"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        placeholder="증명서 인증코드를 입력하세요"
                        required
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="이름"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="이름을 입력하세요"
                          required
                        />
                        <Input
                          label="생년월일"
                          name="birthDate"
                          type="date"
                          value={formData.birthDate}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </>
                  )}

                  <Button type="submit" loading={loading} className="w-full">
                    <i className="ri-search-line"></i>
                    증명서 조회하기
                  </Button>
                </form>
              </Card>
            </div>
          ) : (
            <div className="space-y-8">
              {/* 증명서 정보 */}
              <Card>
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">증명서 조회 결과</h2>
                    <p className="text-gray-600">증명서 ID: {certificate.id}</p>
                  </div>
                  <div className="flex space-x-2">
                    <Button onClick={handleDownload} size="sm">
                      <i className="ri-download-line"></i>
                      PDF 다운로드
                    </Button>
                    <Button onClick={handlePrint} variant="outline" size="sm">
                      <i className="ri-printer-line"></i>
                      인쇄하기
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">수료자 정보</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">이름:</span>
                        <span className="font-medium">{certificate.studentName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">수료일:</span>
                        <span className="font-medium">{certificate.completionDate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">성적:</span>
                        <span className="font-medium text-green-600">{certificate.grade}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">과정 정보</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">과정명:</span>
                        <span className="font-medium">{certificate.courseName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">교육기관:</span>
                        <span className="font-medium">{certificate.institution}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">발급일:</span>
                        <span className="font-medium">{certificate.issueDate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">유효기간:</span>
                        <span className="font-medium">{certificate.validUntil}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {certificate.isPaid && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-information-line text-yellow-600 mr-2"></i>
                      <span className="text-yellow-800">
                        다운로드 가능 횟수: {certificate.maxDownloads - certificate.downloadCount}회 남음
                      </span>
                    </div>
                  </div>
                )}
              </Card>

              {/* 증명서 미리보기 */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">증명서 미리보기</h3>
                <div className="border-2 border-gray-300 rounded-lg p-8 bg-white text-center">
                  <div className="max-w-2xl mx-auto">
                    <div className="mb-8">
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">수료증</h2>
                      <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
                    </div>
                    
                    <div className="space-y-6 text-lg">
                      <p>위 사람은</p>
                      <p className="text-2xl font-bold text-blue-600">{certificate.courseName}</p>
                      <p>과정을 성실히 이수하였기에 이 증서를 수여합니다.</p>
                      
                      <div className="grid grid-cols-2 gap-8 mt-12">
                        <div>
                          <p className="text-gray-600">수료자</p>
                          <p className="text-xl font-bold border-b-2 border-gray-300 pb-2 mt-2">
                            {certificate.studentName}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">수료일</p>
                          <p className="text-xl font-bold border-b-2 border-gray-300 pb-2 mt-2">
                            {certificate.completionDate}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-12">
                        <p className="text-gray-600">{certificate.issueDate}</p>
                        <p className="text-xl font-bold mt-4">{certificate.institution}</p>
                        <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mt-4 flex items-center justify-center">
                          <span className="text-xs text-gray-500">직인</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <div className="text-center">
                <Button 
                  onClick={() => setCertificate(null)} 
                  variant="outline"
                >
                  <i className="ri-arrow-left-line"></i>
                  다른 증명서 조회하기
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
